/**
 * SpeakIQ — talk-to-type for notes. The free, local equivalent of Wispr Flow.
 *
 * Named to match DialIQ and NoteIQ. Deliberately NOT "VoiceIQ": this product
 * already has AI voice calling, voice clones and ElevenLabs, and that name would
 * read as the synthetic-voice feature rather than the agent's own dictation.
 *
 * Both halves already exist in this app, which is why this is small:
 *   speech → text     transcribe.worker.ts (Whisper, WebGPU/WASM)
 *   text → clean text Gemini Nano via the Prompt API
 *
 * Nothing is uploaded. The microphone audio is decoded, transcribed and tidied
 * inside the tab, and only the finished sentence reaches the editor.
 *
 * WHY PRESS-TO-TALK AND NOT LIVE STREAMING: Whisper works on ~30s windows, so a
 * streaming version cuts words at chunk boundaries and shows the agent text that
 * changes under them as later chunks arrive. Recording a thought, then inserting
 * one clean sentence, is both simpler and closer to what dictation users expect.
 */
import { draftCleanup } from "@/app/_module/services/localAI";

/** Hard stop for one SpeakIQ take. Long enough for a paragraph, short enough that
 *  a forgotten open mic cannot record the rest of someone's day. */
export const SPEAKIQ_MAX_SECONDS = 180;

export type SpeakIQStage =
  | { stage: "requesting-mic" }
  | { stage: "listening"; seconds: number }
  | { stage: "transcribing" }
  | { stage: "polishing" }
  | { stage: "done" };

export class SpeakIQError extends Error {
  constructor(
    public readonly reason: "no-mic" | "denied" | "no-speech" | "unsupported" | "failed",
    message: string,
  ) {
    super(message);
  }
}

export function speakIQSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined" &&
    typeof Worker !== "undefined" &&
    typeof OfflineAudioContext !== "undefined"
  );
}

/** Decode recorded audio to the 16kHz mono Float32Array the worker expects. */
async function decodeTo16kMono(buf: ArrayBuffer): Promise<Float32Array> {
  const AC: typeof AudioContext = window.AudioContext || (window as any).webkitAudioContext;
  const probe = new AC();
  const decoded = await probe.decodeAudioData(buf.slice(0));
  await probe.close();
  const offline = new OfflineAudioContext(1, Math.ceil(decoded.duration * 16000), 16000);
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  src.start();
  return (await offline.startRendering()).getChannelData(0);
}

async function pickDevices(): Promise<Array<"webgpu" | "wasm">> {
  try {
    if ((navigator as any).gpu && (await (navigator as any).gpu.requestAdapter())) {
      return ["webgpu", "wasm"];
    }
  } catch {
    /* no adapter */
  }
  return ["wasm"];
}

export interface SpeakIQSession {
  /** Stop recording and resolve with the finished text. */
  stop(): Promise<string>;
  /** Abandon it — releases the mic, resolves stop() with "". */
  cancel(): void;
}

/**
 * Start listening. Returns a handle; call stop() to get the text.
 *
 * @param onStage progress for the UI
 * @param polish  run the Nano cleanup pass (punctuation, filler removal)
 */
export async function startSpeakIQ(
  onStage: (s: SpeakIQStage) => void,
  polish = true,
): Promise<SpeakIQSession> {
  if (!speakIQSupported()) {
    throw new SpeakIQError("unsupported", "This browser can't record audio.");
  }

  onStage({ stage: "requesting-mic" });
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      // Browser-side cleanup before Whisper ever sees it — free accuracy.
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
  } catch (e: any) {
    const denied = e?.name === "NotAllowedError" || e?.name === "SecurityError";
    throw new SpeakIQError(
      denied ? "denied" : "no-mic",
      denied
        ? "Microphone access was blocked. Allow it in your browser's address bar."
        : "No microphone found.",
    );
  }

  const chunks: BlobPart[] = [];
  const recorder = new MediaRecorder(stream);
  recorder.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
  recorder.start();

  const startedAt = Date.now();
  let cancelled = false;
  const tick = setInterval(() => {
    const seconds = Math.round((Date.now() - startedAt) / 1000);
    onStage({ stage: "listening", seconds });
    if (seconds >= SPEAKIQ_MAX_SECONDS && recorder.state === "recording") recorder.stop();
  }, 250);

  // Always release the mic — a live indicator left on in the tab bar is alarming
  // and, on some machines, keeps the device busy for other apps.
  const release = () => {
    clearInterval(tick);
    stream.getTracks().forEach((t) => t.stop());
  };

  const finished = new Promise<Blob>((resolve) => {
    recorder.onstop = () => resolve(new Blob(chunks, { type: recorder.mimeType || "audio/webm" }));
  });

  return {
    cancel() {
      cancelled = true;
      if (recorder.state === "recording") recorder.stop();
      release();
    },
    async stop() {
      if (recorder.state === "recording") recorder.stop();
      const blob = await finished;
      release();
      if (cancelled) return "";

      onStage({ stage: "transcribing" });
      const audio = await decodeTo16kMono(await blob.arrayBuffer());
      if (audio.length < 16000 * 0.4) {
        throw new SpeakIQError("no-speech", "Didn't catch that — try again.");
      }

      const devices = await pickDevices();
      // Same specifier localCallNotes.ts uses. This file lives beside it ON
      // PURPOSE: a `new URL()` worker path that crosses into the
      // `(authenticated)` route group is exactly the kind of thing bundlers
      // resolve at build time and fail on at run time, and there is no reason
      // to be the one call site that tests that.
      const worker = new Worker(new URL("../components/transcribe.worker.ts", import.meta.url));
      let raw = "";
      try {
        const segments = await new Promise<Array<{ text: string }>>((resolve, reject) => {
          worker.onmessage = (e: MessageEvent) => {
            const m = e.data;
            if (m?.type === "done") resolve(m.segments || []);
            else if (m?.type === "error") reject(new SpeakIQError("failed", m.message));
          };
          worker.onerror = (err) =>
            reject(new SpeakIQError("failed", err.message || "Transcription failed"));
          worker.postMessage({ audio, devices }, [audio.buffer]);
        });
        raw = segments.map((s) => s.text).join(" ").replace(/\s+/g, " ").trim();
      } finally {
        worker.terminate();
      }

      if (!raw) throw new SpeakIQError("no-speech", "Didn't catch that — try again.");
      if (!polish) return raw;

      onStage({ stage: "polishing" });
      // Cleanup is a bonus, never a gate: if Nano is unavailable the agent still
      // gets their words, just with Whisper's punctuation.
      const cleaned = await draftCleanup(raw).catch(() => null);
      onStage({ stage: "done" });
      return cleaned || raw;
    },
  };
}
